import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { Actions, ofType } from "@ngrx/effects";
import {
  IAMAccountActions,
  IAMAccountFacade
} from "@youm-front/iam-account/data-access";
import {
  IAMAccountPasswordUpdateCmd,
  IAMAccountRawPassword
} from "@youm-front/iam-account/domain";
import { BehaviorSubject, of, Subject } from "rxjs";
import { delay, switchMap, takeUntil, tap } from "rxjs/operators";

@Component({
  selector: "app-iam-account-feature-password-update",
  templateUrl: "./password-update.component.html",
  styleUrls: ["./password-update.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordUpdateComponent implements OnInit, OnDestroy {
  @ViewChild("passwordUpdateForm") passwordUpdateForm!: NgForm;

  readonly renderUpdateSuccessMsg$ = new BehaviorSubject<boolean>(false);
  readonly renderUpdateBtn$ = new BehaviorSubject<boolean>(true);

  readonly newly = new FormControl<null | string>(null, Validators.required);
  readonly confirm = new FormControl<null | string>(null, Validators.required);
  readonly passwordUpdate = new FormGroup(
    {
      current: new FormControl<null | string>(null, Validators.required),
      newly: this.newly,
      confirm: this.confirm
    },
    [PasswordUpdateComponent.match(this.newly, this.confirm)]
  );

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly actions$: Actions,
    private readonly iamAccountFacade: IAMAccountFacade
  ) {}

  private static match(
    control1: AbstractControl,
    control2: AbstractControl
  ): ValidatorFn {
    const matchErrorKey = "match";
    const matchError = { [matchErrorKey]: { value: false } };

    return (control: AbstractControl): ValidationErrors | null => {
      const control1Value = control1?.value;
      const control2Value = control2?.value;

      const match = control1Value === control2Value;
      if (match) {
        // eslint-disable-next-line  @typescript-eslint/no-unused-vars
        const { [matchErrorKey]: _match1, ...rest1 } = control1.errors || {};
        control1.setErrors(Object.keys(rest1).length ? rest1 : null);

        // eslint-disable-next-line  @typescript-eslint/no-unused-vars
        const { [matchErrorKey]: _match2, ...rest2 } = control2.errors || {};
        control2.setErrors(Object.keys(rest2).length ? rest2 : null);

        return control.errors;
      }

      if (control1?.dirty && control2Value) {
        control1?.setErrors(matchError);
      }

      if (control2?.dirty) {
        control2?.setErrors(matchError);
      }

      return control.errors;
    };
  }

  ngOnInit(): void {
    this.actions$
      .pipe(
        ofType(IAMAccountActions.iamAccountPasswordUpdateSuccess),
        switchMap(() => this.onIAMAccountPasswordUpdateSuccess$()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isLoading$() {
    return this.iamAccountFacade.passwordUpdateIsLoading$;
  }

  onPasswordUpdate() {
    let { current, newly } = this.passwordUpdate.value;

    current = current?.trim();
    newly = newly?.trim();

    if (!current || !newly) {
      return;
    }

    this.iamAccountFacade.iamAccountPasswordUpdate(
      new IAMAccountPasswordUpdateCmd({
        currentPassword: current as IAMAccountRawPassword,
        newPassword: newly as IAMAccountRawPassword
      })
    );
  }

  private onIAMAccountPasswordUpdateSuccess$() {
    return of(null).pipe(
      tap(() => {
        this.passwordUpdateForm.resetForm();
      }),
      tap(() => {
        this.renderUpdateSuccessMsg$.next(true);
        this.renderUpdateBtn$.next(false);
      }),
      delay(5_000),
      tap(() => {
        this.renderUpdateSuccessMsg$.next(false);
        this.renderUpdateBtn$.next(true);
      })
    );
  }
}
